"use client";

import { c as _c } from "react/compiler-runtime";
import type { Product } from "@spree/sdk";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { memo } from "react";
import { ProductImage } from "@/components/ui/product-image";
import { trackSelectItem } from "@/lib/analytics/gtm";
import { salePricing } from "@/lib/utils/pricing";
import { getCardImageUrl } from "@/lib/utils/product-image";
interface ProductCardProps {
  product: Product;
  basePath?: string;
  categoryId?: string;
  index?: number;
  listId?: string;
  listName?: string;
  fetchPriority?: "high" | "low" | "auto";
  /** Optional currency used for analytics; omit to skip the select_item event. */
  currency?: string;
}
export const ProductCard = memo(function ProductCard(t0) {
  const $ = _c(44);
  const {
    product,
    basePath: t1,
    categoryId,
    index,
    listId,
    listName,
    fetchPriority,
    currency
  } = t0;
  const basePath = t1 === undefined ? "" : t1;
  const t = useTranslations("products");
  let t2;
  if ($[0] !== product) {
    t2 = getCardImageUrl(product);
    $[0] = product;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const imageUrl = t2;
  const locale = useLocale();
  let t3;
  if ($[2] !== locale || $[3] !== product.original_price || $[4] !== product.price) {
    t3 = salePricing(product.price, product.original_price, locale);
    $[2] = locale;
    $[3] = product.original_price;
    $[4] = product.price;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    price: displayPrice,
    strikethrough: strikethroughPrice
  } = t3;
  const onSale = strikethroughPrice != null;
  let t4;
  if ($[6] !== currency || $[7] !== index || $[8] !== listId || $[9] !== listName || $[10] !== product) {
    t4 = () => {
      if (index != null && listId && listName && currency) {
        trackSelectItem(product, listId, listName, index, currency);
      }
    };
    $[6] = currency;
    $[7] = index;
    $[8] = listId;
    $[9] = listName;
    $[10] = product;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleClick = t4;
  const t5 = `${basePath}/products/${product.slug}${categoryId ? `?category_id=${categoryId}` : ""}`;
  let t6;
  if ($[12] !== fetchPriority || $[13] !== imageUrl || $[14] !== product.name) {
    t6 = <ProductImage src={imageUrl} alt={product.name} fill={true} className="object-cover group-hover:scale-105 transition-transform duration-300" sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 300px" iconClassName="w-16 h-16" fetchPriority={fetchPriority} />;
    $[12] = fetchPriority;
    $[13] = imageUrl;
    $[14] = product.name;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== onSale || $[17] !== t) {
    t7 = onSale && <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-medium px-2 py-1 rounded">{t("sale")}</span>;
    $[16] = onSale;
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <div className="relative aspect-square bg-gray-100 rounded-md overflow-hidden">{t6}{t7}</div>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== product.name) {
    t9 = <h3 className="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors line-clamp-2">{product.name}</h3>;
    $[22] = product.name;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== displayPrice) {
    t10 = displayPrice && <span className="text-lg font-semibold text-gray-900">{displayPrice}</span>;
    $[24] = displayPrice;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== onSale || $[27] !== strikethroughPrice) {
    t11 = onSale && strikethroughPrice && <span className="text-sm text-gray-500 line-through">{strikethroughPrice}</span>;
    $[26] = onSale;
    $[27] = strikethroughPrice;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <div className="mt-2 flex items-center gap-2">{t10}{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== product.purchasable || $[33] !== t) {
    t13 = !product.purchasable && <span className="mt-2 text-sm text-gray-500">{t("outOfStock")}</span>;
    $[32] = product.purchasable;
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13 || $[37] !== t9) {
    t14 = <div className="p-4">{t9}{t12}{t13}</div>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== handleClick || $[40] !== t14 || $[41] !== t5 || $[42] !== t8) {
    t15 = <Link href={t5} className="group block" onClick={handleClick}>{t8}{t14}</Link>;
    $[39] = handleClick;
    $[40] = t14;
    $[41] = t5;
    $[42] = t8;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
});